import React from 'react';
import { TFunction } from 'react-i18next';
import { FormikProps } from 'formik';
import {
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { FormValidator } from '@piwikpro/form-crate';
import { ChangePasswordAttributes } from '@piwikpro/users-crate';
import {
  Form,
  TextFieldControl,
  Layout,
  Card,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import { UnsavedChangesTracker as UnsavedChangesTrackerComponent } from '@piwikpro/form-crate/components/UnsavedChangesTracker';
import { AuthSettings } from '@piwikpro/auth-crate';

export interface IPasswordChangeProps {
  authService: AuthSettings
  validation: FormValidator
  t: TFunction
  UnsavedChangesTracker: typeof UnsavedChangesTrackerComponent
}

export const PasswordChange: React.FC<IPasswordChangeProps> = ({
  authService,
  validation,
  t,
  UnsavedChangesTracker,
}) => {
  const handleSubmit = async (
    values: ChangePasswordAttributes,
    api: FormikProps<ChangePasswordAttributes>,
  ) => {
    try {
      await authService.changePassword({
        ...values,
      });

      api.resetForm();
    } catch (error: any) {
      api.setErrors({
        currentPassword: error.errors?.detail,
      });
    } finally {
      api.setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Layout.AnnotatedSection
        title={t('security.passwordReset.title')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <Form
              id="reset-password-form"
              initialValues={{
                currentPassword: '',
                newPassword: '',
                newPasswordRepeated: '',
              }}
              validationSchema={validation.createValidationSchema(validators => ({
                currentPassword: validators.userPanel.currentPassword,
                newPassword: validators.userPanel.newPassword,
                newPasswordRepeated: validators.userPanel.newPasswordRepeated,
              }))}
              onSubmit={handleSubmit}
            >
              <Stack vertical spacing="wide">
                <Stack.Item fill>
                  <TextFieldControl
                    label={t('security.passwordReset.labels.currentPassword')}
                    name="currentPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                  />
                </Stack.Item>
                <Stack.Item fill>
                  <TextFieldControl
                    label={t('security.passwordReset.labels.newPassword')}
                    name="newPassword"
                    type="password"
                    info={t('password.should.contain.form.info')}
                    autoComplete="new-password"
                    required
                  />
                </Stack.Item>
                <Stack.Item fill>
                  <TextFieldControl
                    label={t('security.passwordReset.labels.newPasswordConfirm')}
                    name="newPasswordRepeated"
                    type="password"
                    autoComplete="new-password"
                    required
                  />
                </Stack.Item>
                <Stack.Item>
                  <UnsavedChangesTracker render={Api => <Api.UnsavedChangesModal />} type="twoOptions" />
                  <Form.Submit text={t('components:save')} />
                </Stack.Item>
              </Stack>
            </Form>
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
  );
};

export default withInject({
  authService: 'AuthCrate.authSettings',
  config: 'config',
  validation: 'FormCrate.validation',
  Title: 'TitleCrate.components.Title',
  UnsavedChangesTracker: 'FormCrate.components.UnsavedChangesTracker',
})(withTranslation(['profile', 'platform'])(errorBoundary(PasswordChange)));
